import { useMemo, useState } from 'react'
import { AppButton } from '../../ui/AppButton'
import { BuxusSelect } from '../../components/BuxusSelect'
import { requestTypeNeedsCategory, requestTypeOptions } from './requestFormOptions'
import { makeARequestErrorMessage, makeARequestSucceeded, submitMakeARequest } from './makeRequestApi'
import {
  trackFormSubmitted,
  trackButtonPressed,
  type ScreenInteractionContext,
} from '../../platform/analytics/interactionAnalytics'
import { reportOperationalFailure } from '../../platform/errors/webFailureReporting'

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
]

type Props = {
  operatorId: string
  requestType: string
  siteExplanation?: string
  /** Label for the back / done control after submit or when canceling. */
  doneLabel?: string
  onCancel: () => void
  analytics?: ScreenInteractionContext
}

export function RequestFormPanel({
  operatorId,
  requestType,
  siteExplanation,
  doneLabel = 'Back to Make a Request',
  onCancel,
  analytics,
}: Props) {
  const [requestDetails, setRequestDetails] = useState('')
  const [request, setRequest] = useState('')
  const [useAddress, setUseAddress] = useState(true)
  const [addressLine1, setAddressLine1] = useState('')
  const [addressLine2, setAddressLine2] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [county, setCounty] = useState('')
  const [zip, setZip] = useState('')
  const [lat, setLat] = useState('')
  const [long, setLong] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const typeOptions = useMemo(() => requestTypeOptions(requestType), [requestType])
  const needsCategory = requestTypeNeedsCategory(requestType)
  const isRegisterSite = requestType === 'Register an Identified Site'
  const formLocked = submitted || submitting

  const siteLocationValid = !isRegisterSite
    ? true
    : useAddress
      ? !!(addressLine1.trim() && city.trim() && state.trim() && zip.trim())
      : !!(lat.trim() && long.trim())

  const isValid =
    request.trim().length > 0 &&
    (!needsCategory || requestDetails.trim().length > 0) &&
    siteLocationValid

  const handleSubmit = async () => {
    if (!isValid || submitting || submitted) return
    setSubmitting(true)
    setError(null)

    let requestText = request.trim()
    if (isRegisterSite) {
      const location = useAddress
        ? [addressLine1, addressLine2, city, state, county, zip].filter((x) => x.trim()).join(' ')
        : `${long.trim()} ${lat.trim()}`
      requestText = `${requestText} ${location}`.trim()
    }

    try {
      const response = await submitMakeARequest({
        requestType,
        requestDetails: needsCategory ? requestDetails : undefined,
        operatorId,
        request: requestText,
      })
      if (makeARequestSucceeded(response)) {
        if (analytics) {
          trackFormSubmitted({
            ...analytics,
            request_type: requestType,
          })
        }
        setSubmitted(true)
        return
      }
      setError(makeARequestErrorMessage(response) ?? 'Unable to submit this request. Please try again.')
    } catch (error) {
      reportOperationalFailure(error, {
        context: 'makeARequest',
        metadata: {
          request: { requestType, operatorId },
        },
      })
      setError('Unable to submit this request. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section
      className={`request-form-panel${submitted ? ' request-form-panel--submitted' : ''}`}
      aria-live={submitted ? 'polite' : undefined}
    >
      <header className="request-form-header">
        <h2 className="request-form-title">{requestType}</h2>
      </header>

      <fieldset className="request-form-fields" disabled={formLocked}>
        {isRegisterSite && siteExplanation ? (
          <p className="request-form-note">{siteExplanation}</p>
        ) : null}

        {needsCategory ? (
          <label className="request-form-field">
            <span className="request-form-label">Type</span>
            <BuxusSelect
              value={requestDetails}
              onChange={(event) => setRequestDetails(event.target.value)}
            >
              <option value="">Select type…</option>
              {typeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </BuxusSelect>
          </label>
        ) : null}

        <label className="request-form-field">
          <span className="request-form-label">Request</span>
          <textarea
            className="request-form-textarea"
            rows={8}
            value={request}
            onChange={(event) => setRequest(event.target.value)}
            placeholder="Describe your request"
          />
        </label>

        {isRegisterSite ? (
          <div className="request-form-site-block">
            {useAddress ? (
              <>
                <label className="request-form-field">
                  <span className="request-form-label">Address line 1</span>
                  <input className="request-form-input" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} />
                </label>
                <label className="request-form-field">
                  <span className="request-form-label">Address line 2</span>
                  <input className="request-form-input" value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} />
                </label>
                <label className="request-form-field">
                  <span className="request-form-label">City</span>
                  <input className="request-form-input" value={city} onChange={(e) => setCity(e.target.value)} />
                </label>
                <label className="request-form-field">
                  <span className="request-form-label">State</span>
                  <BuxusSelect value={state} onChange={(e) => setState(e.target.value)}>
                    <option value="">Select state…</option>
                    {US_STATES.map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </BuxusSelect>
                </label>
                <label className="request-form-field">
                  <span className="request-form-label">County</span>
                  <input className="request-form-input" value={county} onChange={(e) => setCounty(e.target.value)} />
                </label>
                <label className="request-form-field">
                  <span className="request-form-label">Zip</span>
                  <input className="request-form-input" value={zip} onChange={(e) => setZip(e.target.value)} />
                </label>
              </>
            ) : (
              <>
                <label className="request-form-field">
                  <span className="request-form-label">Latitude</span>
                  <input className="request-form-input" value={lat} onChange={(e) => setLat(e.target.value)} />
                </label>
                <label className="request-form-field">
                  <span className="request-form-label">Longitude</span>
                  <input className="request-form-input" value={long} onChange={(e) => setLong(e.target.value)} />
                </label>
              </>
            )}
            <button
              type="button"
              className="request-form-toggle"
              onClick={() => setUseAddress((v) => !v)}
              disabled={formLocked}
            >
              Enter {useAddress ? 'lat/lng' : 'address'} instead
            </button>
          </div>
        ) : null}
      </fieldset>

      {error && !submitted ? <p className="request-form-error">{error}</p> : null}

      {submitted ? (
        <p className="request-form-submitted-notice">
          Your request has been submitted.
        </p>
      ) : null}

      <div className="request-form-actions">
        <AppButton
          variant="outline"
          disabled={submitting}
          onClick={() => {
            if (analytics) {
              trackButtonPressed({
                ...analytics,
                button: submitted ? doneLabel : 'Back',
                ...(requestType ? { request_type: requestType } : {}),
              })
            }
            onCancel()
          }}
        >
          {submitted ? doneLabel : 'Back'}
        </AppButton>
        <AppButton
          disabled={!isValid || submitted}
          loading={submitting}
          loadingLabel="Submitting…"
          onClick={() => void handleSubmit()}
        >
          Submit
        </AppButton>
      </div>
    </section>
  )
}
